import { useEffect, useState } from "react";
import { api, formatTZS, formatDate, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Users, Calendar, Phone, Mail } from "lucide-react";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const badge = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-emerald-100 text-emerald-700", completed: "bg-slate-200 text-slate-700", cancelled: "bg-red-100 text-red-700" };

export default function ServiceBookingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/service-bookings"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    setSaving(id);
    try {
      await api.post(`/admin/service-bookings/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand)]" /></div>;

  return (
    <div className="space-y-6" data-testid="service-bookings-page">
      <div>
        <h1 className="font-heading text-3xl text-[color:var(--deep)]">Service &amp; Facility Bookings</h1>
        <p className="text-sm text-[color:var(--muted-text)] mt-1">Meeting halls, private dinners, transfers — booked by guests from the landing page.</p>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border text-center text-sm text-[color:var(--muted-text)]" data-testid="empty-service-bookings">No service bookings yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-[color:var(--brand)]/10 p-5" data-testid={`svc-booking-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-[color:var(--muted-text)]">{b.reference}</p>
                  <p className="font-heading text-lg text-[color:var(--deep)] mt-0.5">{b.service_title}</p>
                </div>
                <Badge className={`${badge[b.status] || ""} border-0 capitalize`}>{b.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-sm font-ui">
                <div className="flex items-center gap-2 text-[color:var(--deep)]"><Users className="w-3.5 h-3.5 text-[color:var(--brand)]" /> {b.guest_name}</div>
                <div className="flex items-center gap-2 text-[color:var(--deep)]"><Calendar className="w-3.5 h-3.5 text-[color:var(--brand)]" /> {formatDate(b.event_date)}</div>
                <div className="flex items-center gap-2 text-[color:var(--muted-text)] text-xs"><Phone className="w-3 h-3" /> {b.guest_phone}</div>
                <div className="flex items-center gap-2 text-[color:var(--muted-text)] text-xs truncate"><Mail className="w-3 h-3" /> {b.guest_email}</div>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-[color:var(--muted-text)]">{b.guests} guests</span>
                <span className="font-semibold text-[color:var(--brand)]">{b.price_at_reception ? "Price at reception" : formatTZS(b.service_price || 0)}</span>
              </div>
              {(b.includes || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {b.includes.map((t, i) => <span key={i} className="text-[11px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
              )}
              {b.notes && <p className="mt-3 text-xs text-[color:var(--muted-text)] italic">"{b.notes}"</p>}
              <div className="flex flex-wrap gap-2 mt-4">
                {STATUSES.filter((s) => s !== b.status).map((s) => (
                  <Button key={s} size="sm" variant="outline" disabled={saving === b.id} onClick={() => changeStatus(b.id, s)} data-testid={`svc-set-${s}-${b.id}`} className="capitalize text-xs">
                    Mark {s}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
